import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Sparkles, Star, Zap, MessageCircle, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../contexts/LanguageContext";
import { waLink, telLink } from "../lib/contact";

export default function Hero({ onOpenLead }) {
  const { t } = useLang();

  return (
    <section
      id="top"
      className="relative pt-24 md:pt-36 pb-20 md:pb-28 overflow-hidden bg-[#050814] text-white"
      data-testid="hero-section"
    >
      {/* Background visuals */}
      <div className="absolute inset-0 opacity-60">
        <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-blue-600/40 blur-3xl" />
        <div className="absolute top-20 -right-40 w-[40rem] h-[40rem] rounded-full bg-violet-600/40 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[44rem] h-[18rem] rounded-full bg-fuchsia-500/20 blur-3xl" />
      </div>
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass-dark text-xs font-semibold tracking-wide"
        >
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          {t.hero.eyebrow}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display mt-5 text-[40px] leading-[1.05] sm:text-6xl lg:text-[80px] lg:leading-[0.98] tracking-tight font-bold max-w-5xl"
        >
          {t.hero.titleA}
          <br />
          <span className="text-gradient">{t.hero.titleB}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <Button
            onClick={onOpenLead}
            data-testid="hero-primary-cta"
            className="group h-13 px-7 py-6 rounded-full text-base font-semibold bg-white text-slate-950 hover:bg-white/90 shadow-[0_10px_40px_-10px_rgba(255,255,255,0.5)]"
          >
            {t.hero.ctaPrimary}
            <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition" />
          </Button>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10 text-base font-semibold backdrop-blur transition"
          >
            <MessageCircle className="w-5 h-5 text-emerald-400" />
            {t.hero.ctaSecondary}
          </a>
          <a
            href={telLink()}
            data-testid="hero-call-cta"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10 text-base font-semibold backdrop-blur transition"
          >
            <Phone className="w-5 h-5 text-blue-400" />
            {t.hero.callNow}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.6 }}
          className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-2 text-sm text-slate-400"
        >
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span>{t.hero.trustA}</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-blue-400" />
            <span>{t.hero.trustB}</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-400" />
            <span>{t.hero.trustC}</span>
          </div>
        </motion.div>

        {/* Floating realistic device scene */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.9, ease: "easeOut" }}
          className="relative mt-16 md:mt-24"
        >
          <div className="relative mx-auto max-w-6xl">
            {/* === LAPTOP === */}
            <div className="relative mx-auto w-full max-w-4xl">
              <div className="relative rounded-[20px] md:rounded-[24px] bg-gradient-to-b from-slate-700 to-slate-900 p-2 md:p-3 shadow-[0_60px_140px_-30px_rgba(59,130,246,0.55)] border border-white/10">
                <div className="rounded-[14px] md:rounded-[18px] overflow-hidden bg-slate-950 border border-white/5">
                  {/* Browser chrome */}
                  <div className="bg-slate-900/90 backdrop-blur-md px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                    <div className="ml-3 flex-1 max-w-md mx-auto">
                      <div className="h-6 rounded-md bg-slate-800/80 flex items-center justify-center text-[11px] text-slate-400 font-mono px-3">
                        🔒 launchmitra.in/bombay-brew
                      </div>
                    </div>
                    <div className="w-6 h-6 rounded-md bg-slate-800/80 hidden md:block" />
                  </div>

                  {/* Website content (real cafe-style hero) */}
                  <div className="relative h-[260px] md:h-[420px] overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1559925393-8be0ec4767c8?auto=format&fit=crop&w=1600&q=80"
                      alt="Bombay Brew Cafe website preview"
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/85 via-slate-950/40 to-transparent" />

                    {/* Cafe site nav */}
                    <div className="absolute top-0 inset-x-0 px-6 md:px-10 py-4 md:py-5 flex items-center justify-between text-white">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center text-slate-950 font-display font-bold text-xs">B</div>
                        <span className="font-display font-bold tracking-tight text-sm md:text-base">Bombay Brew</span>
                      </div>
                      <div className="hidden md:flex items-center gap-6 text-xs text-slate-200">
                        <span>Menu</span>
                        <span>Locations</span>
                        <span>Order Online</span>
                        <span className="px-3 py-1.5 rounded-full bg-amber-500 text-slate-950 font-semibold">Reserve</span>
                      </div>
                    </div>

                    {/* Cafe site hero text */}
                    <div className="absolute bottom-0 inset-x-0 p-6 md:p-10 text-white">
                      <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-amber-300">Mumbai · Since 2019</div>
                      <div className="font-display text-2xl md:text-5xl font-bold mt-1.5 md:mt-2 leading-[1.05] max-w-md">
                        Hand-roasted coffee, served with love.
                      </div>
                      <div className="mt-3 md:mt-5 flex flex-wrap gap-2">
                        <span className="px-3 py-1.5 rounded-full bg-white text-slate-950 text-[11px] md:text-xs font-semibold">Order on WhatsApp</span>
                        <span className="px-3 py-1.5 rounded-full border border-white/40 text-white text-[11px] md:text-xs font-semibold backdrop-blur">View Menu</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Laptop base */}
              <div className="mx-auto h-3 w-[105%] -mt-1 bg-gradient-to-b from-slate-700 to-slate-900 rounded-b-[20px]" />
              <div className="mx-auto h-1 w-24 -mt-1 bg-slate-800 rounded-full" />
            </div>

            {/* === FLOATING PHONE (right) === */}
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 8 }}
              animate={{ opacity: 1, y: 0, rotate: 8 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="hidden md:block absolute -right-4 lg:-right-12 -bottom-12 w-[200px] lg:w-[230px]"
            >
              <div className="relative animate-float">
                <div className="rounded-[36px] bg-slate-900 p-1.5 shadow-2xl border border-white/10">
                  <div className="rounded-[30px] overflow-hidden bg-slate-950 relative">
                    {/* notch */}
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-950 rounded-full z-10" />
                    {/* Instagram-style content */}
                    <div className="aspect-[9/19] bg-gradient-to-b from-rose-500 via-fuchsia-500 to-orange-400 relative">
                      <img
                        src="https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=600&q=80"
                        alt="Instagram post preview"
                        className="absolute inset-0 w-full h-full object-cover opacity-90"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/40" />
                      {/* IG header */}
                      <div className="absolute top-9 inset-x-0 px-3 flex items-center justify-between text-white text-[10px]">
                        <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-amber-400 via-rose-500 to-fuchsia-500 p-[1.5px]">
                            <div className="w-full h-full rounded-full bg-slate-900" />
                          </div>
                          <span className="font-semibold">saanvi.boutique</span>
                        </div>
                        <span>•••</span>
                      </div>
                      {/* IG bottom */}
                      <div className="absolute bottom-3 inset-x-3 text-white">
                        <div className="flex gap-3 text-[14px]">
                          <span>♥</span><span>💬</span><span>↗</span>
                        </div>
                        <div className="text-[10px] mt-2 leading-tight">
                          <span className="font-semibold">saanvi.boutique</span> New collection drops this Friday ✨
                        </div>
                        <div className="text-[9px] text-slate-300 mt-0.5">12,482 likes</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* === FLOATING NOTIFICATION CARDS === */}
            {/* WhatsApp lead notification (top-left) */}
            <motion.div
              initial={{ opacity: 0, x: -30, y: -10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="hidden md:block absolute -left-6 lg:-left-16 top-16 lg:top-20 animate-float"
              style={{ animationDelay: "0.6s" }}
            >
              <div className="rounded-2xl bg-white shadow-2xl border border-slate-200 p-3.5 w-[230px]">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm">✓</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">New WhatsApp lead</div>
                    <div className="text-sm font-semibold text-slate-900 truncate">Anita wants to book</div>
                  </div>
                </div>
                <div className="mt-2.5 px-3 py-2 rounded-xl bg-emerald-50 text-[12px] text-slate-700 leading-snug">
                  "Hi! Saw your site, can I book a table for 4 tonight?"
                </div>
              </div>
            </motion.div>

            {/* Google review card (right top) */}
            <motion.div
              initial={{ opacity: 0, x: 30, y: -10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 1.0, duration: 0.6 }}
              className="hidden lg:block absolute -right-4 top-8 animate-float"
              style={{ animationDelay: "1.4s" }}
            >
              <div className="rounded-2xl bg-white shadow-2xl border border-slate-200 px-4 py-3 w-[210px]">
                <div className="flex items-center gap-2">
                  <svg viewBox="0 0 48 48" className="w-5 h-5">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                    <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C42 35 44 30 44 24c0-1.3-.1-2.3-.4-3.5z"/>
                  </svg>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Google Reviews</span>
                </div>
                <div className="flex items-center gap-1 mt-2">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-amber-400">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                  <span className="text-sm font-bold text-slate-900 ml-1">4.9</span>
                </div>
                <div className="text-[12px] text-slate-600 mt-1">+184 reviews this month</div>
              </div>
            </motion.div>

            {/* Bookings counter (bottom-left) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="hidden md:block absolute -left-2 lg:-left-10 -bottom-8 animate-float"
              style={{ animationDelay: "2s" }}
            >
              <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-2xl px-5 py-3.5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-blue-100 font-semibold">This month</div>
                <div className="font-display text-3xl font-bold mt-0.5">320</div>
                <div className="text-[11px] text-blue-100 -mt-0.5">online bookings</div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll incentive indicator */}
        <motion.a
          href="#how"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="hidden md:flex absolute left-1/2 -translate-x-1/2 bottom-8 flex-col items-center gap-2 group"
          data-testid="hero-scroll-indicator"
          aria-label="Scroll to see more"
        >
          <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 group-hover:text-white transition">
            Scroll to discover
          </span>
          <div className="relative w-6 h-10 rounded-full border border-white/25 group-hover:border-white/50 flex items-start justify-center pt-2 transition">
            <motion.span
              className="block w-1 h-2 rounded-full bg-gradient-to-b from-blue-400 to-violet-400"
              animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400 -mt-1 animate-bounce" />
        </motion.a>
      </div>
    </section>
  );
}
